import apiV2 from '../api/v2';
import type { PurchaseOrderChannel } from './purchaseOrders';

const MARKET_SUFFIX: Record<'US' | 'CA', string> = { US: 'US', CA: 'Canada' };

// orthoarch was Ortho Arch, now retired and merged into Biolux Group - it was
// always just Biolux's Canadian order inbox, never a separate vendor.
function humanizeVendorSlug(vendor: string): string {
    if (vendor === 'orthoarch') return 'Ortho Arch';
    if (vendor === 'biolux') return 'Biolux';
    return vendor;
}

// Events written before the payload carried `market` predate the merge, so
// the vendor slug alone still tells us the market.
const LEGACY_MARKET_BY_VENDOR: Partial<Record<string, 'US' | 'CA'>> = {
    orthoarch: 'CA',
    biolux: 'US'
};

// The vendor slug no longer implies a market post-merge - `market` from the
// event payload is the only reliable discriminator for new orders.
export function poVendorLabel(vendor: string, market?: string | null): string {
    const resolvedMarket =
        market === 'US' || market === 'CA' ? market : LEGACY_MARKET_BY_VENDOR[vendor];
    const name = humanizeVendorSlug(vendor);
    return resolvedMarket ? `${name} (${MARKET_SUFFIX[resolvedMarket]})` : name;
}

export type OrthopulseOrderBlockedLevel = 'warning' | 'error';

export type OrthopulseOrderBlockedReason =
    | 'charge_without_po'
    | 'already_ordered'
    | 'v2_patient'
    | 'vendor_missing'
    | 'vendor_inactive'
    | 'vendor_recipients_missing'
    | 'order_method_missing'
    | 'order_method_inactive'
    // The vendor comes from the patient's country; only the channel comes from
    // the product, so a country's vendor can lack that channel entirely.
    | 'vendor_order_method_missing'
    | 'vendor_part_number_missing'
    | 'external_ordering_required';

export interface OrthopulseOrderBlockedDetail {
    level: OrthopulseOrderBlockedLevel;
    reason: OrthopulseOrderBlockedReason;
    forceable: boolean;
    chargeId?: string;
    poNumber?: string;
    sentAt?: string;
    // Set by the vendor_* reasons and external_ordering_required.
    vendorId?: string;
    // Server-rendered explanation, always populated now. Preferred over every
    // local string so a reason added server-side cannot mis-render here.
    message?: string;
}

export type OrthopulseOrderBlocked = OrthopulseOrderBlockedDetail | null;

// 'external_ordering_required' arrives at level 'error' but is not a failure:
// the PO was raised correctly and is waiting for a human to place it.
export type OrthopulseBlockedTone = 'info' | 'warning' | 'error';

export function orthopulseBlockedTone(
    blocked: NonNullable<OrthopulseOrderBlocked>
): OrthopulseBlockedTone {
    if (blocked.reason === 'external_ordering_required') return 'info';
    return blocked.level === 'warning' ? 'warning' : 'error';
}

export function isExternalOrderingBlock(blocked: OrthopulseOrderBlocked): boolean {
    return blocked?.reason === 'external_ordering_required';
}

// One resolver for every blocked reason. `blocked.message` wins when present,
// so a reason added server-side can never fall through to the wrong sentence.
export function orthopulseBlockedMessage(blocked: NonNullable<OrthopulseOrderBlocked>): string {
    const serverMessage = blocked.message?.trim();
    if (serverMessage) return serverMessage;
    // The server renders every reason; naming the reason beats inventing a
    // second copy of its wording, which is what drifted before.
    return `Ordering is blocked (${blocked.reason}).`;
}

// Only an 'error' block with forceable:true can ever be overridden - dispatch
// hard-rejects everything else (warnings never need it, v2_patient can't).
export function isBlockedBypassable(blocked: OrthopulseOrderBlocked): boolean {
    return !!blocked && blocked.level === 'error' && blocked.forceable;
}

export type ShipToTarget = 'practice' | 'patient';

export interface ShipToPayload {
    target: ShipToTarget;
    name: string;
    address: string;
    address2?: string | null;
    city: string;
    state: string;
    zip: string;
    country: string;
    email?: string | null;
    phone?: string | null;
}

// Practice columns are all nullable in the DB - every field but `name` can
// come back null, not just the ones that look "optional" in a US address.
export interface ShipToPracticeDefaults {
    name: string;
    address: string | null;
    address2: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    country: string | null;
    email: string | null;
    phone: string | null;
}

export interface ShipToPatientDefaults {
    name: string;
    email: string | null;
    phone: string | null;
}

// Patient addresses aren't stored - `patient` never carries address fields,
// the operator types them fresh in the ship-to form.
export interface OrthopulseShipToDefaults {
    practice: ShipToPracticeDefaults;
    patient: ShipToPatientDefaults;
}

// Always 200, even when blocked - the preview still renders the composed
// email so the admin can see exactly what would be sent (see `blocked`).
export interface OrthopulseOrderPreview {
    subject: string;
    html: string;
    from: string;
    recipients: string[];
    // A products.vendor slug - no longer just the two OrthoPulse vendors.
    vendor: string;
    vendorName: string;
    // 'external' is placed on the vendor's site; no email is ever sent.
    ordersVia: PurchaseOrderChannel;
    poNumberPreview: string;
    blocked: OrthopulseOrderBlocked;
    // False when the patient already paid ODO: the send records a $0 charge instead.
    practiceCharged: boolean;
    shipToDefaults: OrthopulseShipToDefaults;
}

export interface OrthopulseOrderResult {
    sent: true;
    poNumber: string;
    recipients: string[];
    vendor: string;
}

// Escape hatch for operators with platform-level billing access: bypasses
// the `blocked` guard server-side. forceReason is required (5-500 chars) and fully audited.
export interface ForceOverridePayload {
    force: true;
    forceReason: string;
}

// Exact match (case-insensitive) against the backend's ORTHOPULSE_PRODUCT_NAME
// - a substring match would also catch e.g. "OrthoPulse Refill" and 422 later.
export function isOrthopulseProductName(name: string | null | undefined): boolean {
    return (name ?? '').trim().toLowerCase() === 'orthopulse';
}

// chargeId targets an existing charge; shipTo/forceOverride are added once
// the ship-to/override steps resolve. dryRun:true never sends/creates anything.
export async function previewOrthopulseOrder(
    patientId: string,
    chargeId?: string,
    shipTo?: ShipToPayload,
    forceOverride?: ForceOverridePayload
): Promise<OrthopulseOrderPreview> {
    const response = await apiV2.post<{ success: boolean; data: OrthopulseOrderPreview }>(
        `/patients/${patientId}/orders/orthopulse`,
        {
            dryRun: true,
            ...(chargeId ? { chargeId } : {}),
            ...(shipTo ? { shipTo } : {}),
            ...(forceOverride ?? {})
        }
    );
    return response.data.data;
}

// Sends the order for real. A blocked patient rejects with 409 here unless
// forceOverride is set and the caller holds platform-level 'billing.update'.
export async function sendOrthopulseOrder(
    patientId: string,
    chargeId?: string,
    shipTo?: ShipToPayload,
    forceOverride?: ForceOverridePayload
): Promise<OrthopulseOrderResult> {
    const response = await apiV2.post<{ success: boolean; data: OrthopulseOrderResult }>(
        `/patients/${patientId}/orders/orthopulse`,
        {
            ...(chargeId ? { chargeId } : {}),
            ...(shipTo ? { shipTo } : {}),
            ...(forceOverride ?? {})
        }
    );
    return response.data.data;
}
